"use client"

import { useEffect } from 'react'

export function PerformanceMonitor() {
  useEffect(() => {
    // Monitor Core Web Vitals
    if (typeof window !== 'undefined' && 'performance' in window) {
      // Track page load performance
      window.addEventListener('load', () => {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming
        
        if (navigation) {
          const metrics = {
            domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
            loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
            totalLoadTime: navigation.loadEventEnd - navigation.fetchStart,
            dnsLookup: navigation.domainLookupEnd - navigation.domainLookupStart,
            tcpConnect: navigation.connectEnd - navigation.connectStart,
            ttfb: navigation.responseStart - navigation.requestStart,
          }
          
          // Log slow performance
          if (metrics.totalLoadTime > 3000) {
            console.warn('⚠️ Slow page load:', {
              page: window.location.pathname,
              totalTime: `${metrics.totalLoadTime}ms`,
              ttfb: `${metrics.ttfb}ms`,
              domContentLoaded: `${metrics.domContentLoaded}ms`
            })
          } else {
            console.log('✅ Fast page load:', `${metrics.totalLoadTime}ms`)
          }
        }
      })

      // Monitor largest contentful paint (LCP)
      if ('PerformanceObserver' in window) {
        try {
          const lcpObserver = new PerformanceObserver((list) => {
            const entries = list.getEntries()
            const lastEntry = entries[entries.length - 1]
            
            if (lastEntry && lastEntry.startTime > 2500) {
              console.warn('⚠️ Poor LCP:', `${lastEntry.startTime.toFixed(2)}ms`)
            }
          })
          
          lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] })

          // Monitor cumulative layout shift (CLS)
          const clsObserver = new PerformanceObserver((list) => {
            let clsValue = 0
            for (const entry of list.getEntries()) {
              if (!(entry as any).hadRecentInput) {
                clsValue += (entry as any).value
              }
            }
            
            if (clsValue > 0.1) {
              console.warn('⚠️ High CLS:', clsValue.toFixed(4))
            }
          })
          
          clsObserver.observe({ entryTypes: ['layout-shift'] })

          // Monitor long tasks
          const longTaskObserver = new PerformanceObserver((list) => {
            for (const entry of list.getEntries()) {
              if (entry.duration > 50) {
                console.warn('⚠️ Long task detected:', `${entry.duration.toFixed(2)}ms`)
              }
            }
          })
          
          if ('longtask' in window) {
            longTaskObserver.observe({ entryTypes: ['longtask'] })
          }
        } catch (error) {
          console.warn('Performance monitoring setup failed:', error)
        }
      }

      // Monitor memory usage (if available)
      const monitorMemory = () => {
        if ('memory' in performance) {
          const memory = (performance as any).memory
          const usedMB = memory.usedJSHeapSize / 1024 / 1024
          const totalMB = memory.totalJSHeapSize / 1024 / 1024
          
          if (usedMB > 50) { // Alert if over 50MB
            console.warn('⚠️ High memory usage:', `${usedMB.toFixed(2)}MB / ${totalMB.toFixed(2)}MB`)
          }
        }
      }

      // Check memory every 30 seconds
      const memoryInterval = setInterval(monitorMemory, 30000)
      
      return () => {
        clearInterval(memoryInterval)
      }
    }
  }, [])

  return null // This component doesn't render anything
} 